import agwaActionBarStyles from './agwa-action-bar.styles.css?inline';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import type { SlDialog } from '@shoelace-style/shoelace';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import type { ActionBarAction, ActionTriggeredDetail } from './types';
import { terms } from '../../language/terms';

@customElement('agwa-action-bar')
export class AgwaActionBar extends LitElement {
  /**
   * Inline density: no frame, no "Actions" caption, no margins - for riding a
   * page header's actions slot instead of owning a row (architect, 2026-09-05:
   * the framed row "takes up an entire row" and was ruled too heavy). The
   * buttons, dialogs and confirm flows are identical in both densities.
   */
  @property({ type: Boolean, reflect: true }) inline = false;

  static styles = unsafeCSS(agwaActionBarStyles);

  @property({ attribute: false }) actions: ActionBarAction[] = [];
  @property({ type: Boolean }) loading: boolean = false;
  @property({ type: Boolean }) compact: boolean = false; // future variant toggle

  // confirmation state
  @state() private pendingAction: ActionBarAction | undefined;
  @state() private note: string = '';

  // keyboard navigation index
  @state() private focusIndex: number = 0;

  @query('#agwaActionDialog') private actionDialog?: SlDialog;

  render() {
    return html`
      <div
        class="bar-wrapper ${this.inline ? 'inline' : ''}"
        role="toolbar"
        @keydown=${this.onKeydown}
        tabindex="0"
      >
        <div class="bar">
          ${
            this.inline
              ? nothing
              : html`<span class="group-label">Actions</span>`
          }
          <div class="actions-container">
            ${
              this.actions.length
                ? this.actions.map((a, i) => this.renderActionButton(a, i))
                : html`<span class="text-muted">No actions available</span>`
            }
          </div>
          <div class="grow"></div>
        </div>
      </div>
      ${this.renderDialog()}
    `;
  }

  private renderActionButton(a: ActionBarAction, index: number) {
    const disabled = this.loading || a.disabled;
    const btn = html`<sl-button
      class="action-button"
      size="small"
      variant="${a.variant || 'default'}"
      ?disabled=${disabled}
      aria-current=${this.focusIndex === index}
      @click=${() => this.handleActionClick(a)}
    >
      ${
        a.icon
          ? html`<sl-icon name="${a.icon}" slot="prefix"></sl-icon>`
          : nothing
      }
      ${a.label}
    </sl-button>`;
    // The tooltip is rendered on a DISABLED action too, because that is where it matters most:
    // a page that withholds an action says why in its tooltip ("Set the Annual Review Date before
    // approving", "Set the Certificate Expiry Date before submitting", @GA-CERT-008/014/016/018).
    // Until 2026-09-26 this rendered the tooltip only on enabled actions, so every such reason was
    // written by its page and never shown to anyone.
    return a.tooltip
      ? html`<sl-tooltip content="${a.tooltip}">${btn}</sl-tooltip>`
      : btn;
  }

  private handleActionClick(action: ActionBarAction) {
    if (this.loading || action.disabled) return;
    if (action.beforeDispatch) {
      const proceed = action.beforeDispatch(action);
      if (proceed === false) return;
    }
    if (action.confirm) {
      this.pendingAction = action;
      this.note = '';
      void this.updateComplete.then(() => {
        void this.actionDialog?.show();
      });
    } else {
      this.dispatchAction(action);
    }
  }

  private dispatchAction(action: ActionBarAction, note?: string) {
    const detail: ActionTriggeredDetail = {
      id: action.id,
      note,
      data: action.data,
      original: action,
    };
    this.dispatchEvent(
      new CustomEvent<ActionTriggeredDetail>('action', {
        detail,
        bubbles: true,
        composed: true,
      })
    );
  }

  private renderDialog() {
    const a = this.pendingAction;
    if (!a || !a.confirm) return nothing;
    const c = a.confirm;
    const max = c.maxLength || 4000;
    return html`<sl-dialog
      id="agwaActionDialog"
      label="${c.title || a.label}"
      @sl-request-close=${(e: CustomEvent) => {
        if (e.detail.source === 'overlay') e.preventDefault();
      }}
    >
      <div class="dialog-body">
        ${
          c.message
            ? html`<p style="margin-top:0; font-size:0.8rem;">${c.message}</p>`
            : nothing
        }
        ${
          c.noteLabel || c.notePlaceholder || c.requireNote
            ? // autocomplete="off" on the note: data entry is never autofilled
              // (data-entry-autofill.md), and a raw textarea is outside the reach of the
              // initializer that covers every sl-textarea, so it says so itself.
              html`<div class="field-group">
                <label class="note-label"
                  >${
                    c.noteLabel ||
                    (c.requireNote ? 'Note (required)' : 'Note (optional)')
                  }</label
                >
                <textarea
                  class="note-textarea"
                  rows="6"
                  autocomplete="off"
                  .value=${this.note}
                  @input=${(e: Event) =>
                    (this.note = (e.target as HTMLTextAreaElement).value.slice(
                      0,
                      max
                    ))}
                  placeholder="${
                    c.notePlaceholder ||
                    (c.requireNote
                      ? 'Enter a note'
                      : `Optional note for ${terms.auditor.lowerPlural} / reviewers`)
                  }"
                  ?required=${c.requireNote}
                ></textarea>
                <div class="note-meta">
                  <span>${this.note.length} / ${max}</span>
                  <span
                    >${c.requireNote ? 'Required.' : 'Optional.'} Max ${max}
                    chars.</span
                  >
                </div>
              </div>`
            : nothing
        }
      </div>
      <sl-button
        slot="footer"
        variant="default"
        @click=${() => this.closeDialog(false)}
        >Cancel</sl-button
      >
      <sl-button
        slot="footer"
        variant="${a.variant || 'primary'}"
        @click=${() => this.closeDialog(true)}
        >${a.label}</sl-button
      >
    </sl-dialog>`;
  }

  private closeDialog(confirm: boolean) {
    if (confirm && this.pendingAction) {
      const cfg = this.pendingAction.confirm;
      const trimmed = this.note.trim();

      // Validate required note before closing
      if (cfg?.requireNote && !trimmed) {
        // Show validation - focus the textarea and don't close
        const textarea = this.shadowRoot?.querySelector(
          '.note-textarea'
        ) as HTMLTextAreaElement;
        if (textarea) {
          textarea.focus();
          textarea.setCustomValidity('This field is required');
          textarea.reportValidity();
        }
        return; // Don't close dialog
      }

      void this.actionDialog?.hide();

      const note = cfg?.requireNote ? trimmed : trimmed || undefined;
      this.dispatchAction(this.pendingAction, note);
    } else {
      void this.actionDialog?.hide();
    }
    this.pendingAction = undefined;
    this.note = '';
  }

  private onKeydown(e: KeyboardEvent) {
    if (!this.actions.length) return;
    if (['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(e.key)) {
      e.preventDefault();
      const last = this.actions.length - 1;
      if (e.key === 'ArrowRight')
        this.focusIndex = (this.focusIndex + 1) % this.actions.length;
      else if (e.key === 'ArrowLeft')
        this.focusIndex =
          (this.focusIndex - 1 + this.actions.length) % this.actions.length;
      else if (e.key === 'Home') this.focusIndex = 0;
      else if (e.key === 'End') this.focusIndex = last;
      this.focusButton();
    } else if (['Enter', ' '].includes(e.key)) {
      e.preventDefault();
      const a = this.actions[this.focusIndex];
      if (a) this.handleActionClick(a);
    }
  }

  private focusButton() {
    const buttons = this.shadowRoot?.querySelectorAll('.action-button');
    const btn = buttons?.[this.focusIndex] as HTMLButtonElement | undefined;
    btn?.focus();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'agwa-action-bar': AgwaActionBar;
  }
}
